import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './Apply.css';

// Fampack accounts an applicant DMs their verification code to (placeholders).
// Keyed by platform so a stored dm_platform maps straight back to a label and
// the account the applicant was told to message.
const FAMPACK_ACCOUNTS = [
  { key: 'instagram', network: 'Instagram', handle: '[FAMPACK_INSTAGRAM]' },
  { key: 'tiktok', network: 'TikTok', handle: '[FAMPACK_TIKTOK]' },
  { key: 'x', network: 'X', handle: '[FAMPACK_X]' },
];

const accountFor = (key) => FAMPACK_ACCOUNTS.find((a) => a.key === key);

// Range options for the follower-count stage (total across platforms). The
// keys are what the database stores -- the labels are free to change without
// a migration.
const FOLLOWER_RANGES = [
  { key: 'under_1k', label: 'Under 1,000' },
  { key: '1k_5k', label: '1,000 – 5,000' },
  { key: '5k_10k', label: '5,000 – 10,000' },
  { key: '10k_50k', label: '10,000 – 50,000' },
  { key: '50k_100k', label: '50,000 – 100,000' },
  { key: '100k_500k', label: '100,000 – 500,000' },
  { key: '500k_plus', label: '500,000+' },
];

// Ordered input stages — each is its own page. A "verify" stage is appended.
//
// The code stage sits second on purpose. We cannot ask "has this email
// applied before?" until the visitor has proved they own the address —
// anything that answers that earlier would let someone test a list of
// addresses to see who has applied. Verifying first also means a returning
// applicant never refills a form we already have.
const INPUT_STAGES = ['email', 'emailcode', 'socials', 'followers', 'username'];
const SOCIALS_STAGE = INPUT_STAGES.indexOf('socials');

// Mirrors the check constraint on applications.username, which in turn
// mirrors profiles.username -- a stated preference should always be something
// that could actually be granted later.
const USERNAME_RE = /^[a-z0-9]{2,20}$/;

// Matches auth.email.max_frequency in supabase/config.toml.
const RESEND_SECONDS = 60;

// Handles are stored bare: no leading '@', no surrounding whitespace.
const cleanHandle = (v) => v.trim().replace(/^@+/, '');
const cleanUsername = (v) => cleanHandle(v).toLowerCase();

function messageFor(error) {
  if (!error) return 'Something went wrong. Try again.';
  if (error.message && /rate|too many|seconds/i.test(error.message)) {
    return 'Too many attempts. Wait a minute and try again.';
  }
  return error.message || 'Something went wrong. Try again.';
}

function Dots({ total, active }) {
  return (
    <ol className="ap-dots" aria-hidden="true">
      {Array.from({ length: total }).map((_, i) => (
        <li
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className={
            'ap-dot' +
            (i === active ? ' is-active' : '') +
            (i < active ? ' is-done' : '')
          }
        />
      ))}
    </ol>
  );
}

function AgreementLine() {
  return (
    <p className="ap-agreement">
      By continuing, you agree to our <Link to="/terms">Terms of Service</Link>{' '}
      and <Link to="/privacy">Privacy Policy</Link>.
    </p>
  );
}

export default function Apply() {
  const [params] = useSearchParams();
  const prefillEmail = (params.get('email') || '').trim();

  const [data, setData] = useState({
    email: prefillEmail,
    instagram: '',
    tiktok: '',
    x: '',
    followers: '',
    usernameChoice: '',
    customUsername: '',
    emailcode: '',
  });
  // Skip the email page when an email was passed in from the landing page.
  const [screen, setScreen] = useState(prefillEmail ? 1 : 0);
  const [lookupValue, setLookupValue] = useState('');
  const [lookupCode, setLookupCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // True once the OTP has been accepted. Past that point the email and code
  // stages are settled, so Back must not walk into them.
  const [verified, setVerified] = useState(false);
  // The stored row, not just the code. The returning-applicant path has no
  // form state to fall back on, so the handles and status have to come from
  // here for every screen after submission.
  const [application, setApplication] = useState(null);
  const [dmPlatform, setDmPlatform] = useState('');

  const inputRef = useRef(null);
  // Errors are cleared by whoever navigates, not here -- otherwise a handler
  // that reports a failure and bounces the user to another stage would have
  // its own message wiped by this effect.
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [screen]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendCode = async (email, { createUser }) => {
    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: createUser },
    });
    if (err) return err;
    setCooldown(RESEND_SECONDS);
    return null;
  };

  // The landing page can hand us an email and skip stage 0, so the code has
  // to go out here instead. The ref guards against StrictMode's double mount
  // in development sending two.
  const prefillSent = useRef(false);
  useEffect(() => {
    if (!prefillEmail || prefillSent.current) return;
    prefillSent.current = true;
    sendCode(prefillEmail, { createUser: true }).then((err) => {
      if (err) {
        setScreen(0);
        setError(messageFor(err));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalDots = INPUT_STAGES.length + 1; // input stages + verify
  const activeDot =
    typeof screen === 'number'
      ? screen
      : screen === 'verify'
        ? INPUT_STAGES.length
        : -1;
  const showDots = typeof screen === 'number' || screen === 'verify';

  const update = (key, value) => setData((d) => ({ ...d, [key]: value }));

  const next = () => {
    if (typeof screen === 'number') {
      setError('');
      setScreen(screen < INPUT_STAGES.length - 1 ? screen + 1 : 'verify');
    }
  };

  const back = () => {
    setError('');
    if (typeof screen === 'number' && screen > 0) {
      // Once verified there is nothing to go back to: the address is settled
      // and the code has been spent.
      const floor = verified ? SOCIALS_STAGE : 0;
      if (screen > floor) setScreen(screen - 1);
    } else if (screen === 'lookupcode') setScreen('lookup');
    else if (screen === 'lookup') setScreen(0);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(application?.dm_code || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — the code is shown on screen anyway */
    }
  };

  const handleOptions = [
    { key: 'instagram', label: 'Instagram', value: cleanHandle(data.instagram) },
    { key: 'tiktok', label: 'TikTok', value: cleanHandle(data.tiktok) },
    { key: 'x', label: 'X', value: cleanHandle(data.x) },
  ].filter((o) => o.value);

  const chosenUsername = () => {
    if (data.usernameChoice === 'custom') return cleanUsername(data.customUsername);
    const opt = handleOptions.find((o) => o.key === data.usernameChoice);
    return opt ? cleanUsername(opt.value) : '';
  };

  const submitStage = async (e) => {
    e.preventDefault();
    const id = INPUT_STAGES[screen];

    if (id === 'email') {
      const email = data.email.trim();
      setBusy(true);
      setError('');
      const err = await sendCode(email, { createUser: true });
      setBusy(false);
      if (err) {
        setError(messageFor(err));
        return;
      }
      update('email', email);
      next();
      return;
    }
    if (
      id === 'socials' &&
      !data.instagram.trim() &&
      !data.tiktok.trim() &&
      !data.x.trim()
    ) {
      setError('Enter at least one handle.');
      return;
    }
    if (id === 'followers' && !data.followers) {
      setError('Select a range.');
      return;
    }
    if (id === 'username') {
      if (!data.usernameChoice) {
        setError('Pick one option.');
        return;
      }
      if (data.usernameChoice === 'custom' && !data.customUsername.trim()) {
        setError('Enter a username.');
        return;
      }
      if (
        data.usernameChoice !== 'custom' &&
        !handleOptions.some((o) => o.key === data.usernameChoice)
      ) {
        setError('Pick one option.');
        return;
      }
      // The database will reject anything else, so say so before they spend
      // a verification code finding out. Handles routinely carry dots and
      // underscores, so the "use my handle" options can land here.
      if (!USERNAME_RE.test(chosenUsername())) {
        setError(
          'Usernames are 2–20 characters, lowercase letters and numbers only. ' +
            'Pick a custom one if your handle has dots or underscores.',
        );
        return;
      }
      // Last stage. The applicant verified back at stage 2, so a session is
      // already in hand and the row can be written here.
      setBusy(true);
      setError('');
      const failure = await storeApplication();
      setBusy(false);
      if (failure) {
        setError(failure);
        return;
      }
      setScreen('verify');
      return;
    }
    next();
  };

  // Everything the screens after submission need. RLS is own-row-only, so no
  // filter is required beyond excluding rejected rows.
  const APPLICATION_COLUMNS =
    'dm_code, status, dm_platform, instagram, tiktok, x';

  // The live application, if any. Rejected rows are excluded: the partial
  // unique index allows at most one non-rejected row per user, so this is
  // always zero or one result, and a user whose every attempt was rejected
  // correctly reads as "no application" and may apply again.
  const fetchActiveApplication = async () => {
    const { data: row } = await supabase
      .from('applications')
      .select(APPLICATION_COLUMNS)
      .neq('status', 'rejected')
      .maybeSingle();
    return row || null;
  };

  // Nothing is written until the email OTP has been verified: the row points
  // at public.profiles, which only exists once the code has been accepted.
  const storeApplication = async () => {
    const { data: userData, error: userErr } = await supabase.auth.getUser();
    if (userErr || !userData?.user) return 'Your session expired. Start again.';

    const { data: inserted, error: insErr } = await supabase
      .from('applications')
      .insert({
        user_id: userData.user.id,
        instagram: cleanHandle(data.instagram) || null,
        tiktok: cleanHandle(data.tiktok) || null,
        x: cleanHandle(data.x) || null,
        follower_range: data.followers,
        username: chosenUsername(),
      })
      .select(APPLICATION_COLUMNS)
      .single();

    if (insErr) {
      // The partial unique index rejects a second live application. Show the
      // one they already have rather than an error.
      if (insErr.code === '23505') {
        const existing = await fetchActiveApplication();
        if (existing) {
          setApplication(existing);
          return null;
        }
      }
      return messageFor(insErr);
    }

    setApplication(inserted);
    return null;
  };

  // Verifying proves they own the address, which is the point at which it
  // becomes safe to look up whether they have applied before.
  const submitEmailCode = async (e) => {
    e.preventDefault();
    const token = data.emailcode.trim();
    if (!/^\d{6}$/.test(token)) {
      setError('Enter the 6-digit code.');
      return;
    }
    setBusy(true);
    setError('');

    const { error: verifyErr } = await supabase.auth.verifyOtp({
      email: data.email.trim(),
      token,
      type: 'email',
    });
    if (verifyErr) {
      setBusy(false);
      setError('That code is not right, or it has expired.');
      return;
    }
    setVerified(true);

    const existing = await fetchActiveApplication();
    setBusy(false);

    if (existing) {
      // Still pending means we were never told the DM went out, so send them
      // to the code screen. Anything else is a state to report.
      setApplication(existing);
      setScreen(existing.status === 'pending' ? 'verify' : 'done');
      return;
    }
    // Nothing live: either brand new, or every previous attempt was rejected
    // and they are free to apply again.
    next();
  };

  const submitLookup = async (e) => {
    e.preventDefault();
    const email = lookupValue.trim();
    setBusy(true);
    setError('');
    // shouldCreateUser: false — an unknown address must not quietly become an
    // account here.
    const err = await sendCode(email, { createUser: false });
    setBusy(false);
    if (err) {
      setError(
        /signups not allowed|not found/i.test(err.message || '')
          ? 'We could not find an application for that email.'
          : messageFor(err),
      );
      return;
    }
    setScreen('lookupcode');
  };

  const submitLookupCode = async (e) => {
    e.preventDefault();
    const token = lookupCode.trim();
    if (!/^\d{6}$/.test(token)) {
      setError('Enter the 6-digit code.');
      return;
    }
    setBusy(true);
    setError('');

    const { error: verifyErr } = await supabase.auth.verifyOtp({
      email: lookupValue.trim(),
      token,
      type: 'email',
    });
    if (verifyErr) {
      setBusy(false);
      setError('That code is not right, or it has expired.');
      return;
    }
    setVerified(true);

    const existing = await fetchActiveApplication();
    setBusy(false);

    if (existing) {
      setApplication(existing);
      // Still pending means we were never told the DM went out, so put them
      // back on the code screen. Anything else is a state to report, not a
      // step to redo.
      setScreen(existing.status === 'pending' ? 'verify' : 'done');
      return;
    }
    // Verified, but nothing live to show — never applied, or every previous
    // attempt was rejected. Drop them into the funnel with the email settled.
    update('email', lookupValue.trim());
    setScreen(SOCIALS_STAGE);
  };

  const resend = async (email, createUser) => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    setError('');
    const err = await sendCode(email, { createUser });
    setBusy(false);
    if (err) setError(messageFor(err));
  };

  // Only the platforms they actually gave a handle for. Taken from the stored
  // row rather than form state, so the returning-applicant path works too.
  const platformOptions = FAMPACK_ACCOUNTS.filter(
    (a) => application && application[a.key],
  );

  // pending -> ready. The RPC is idempotent, so a double tap is harmless.
  const confirmPlatform = async () => {
    if (!dmPlatform) {
      setError('Pick the account you sent it from.');
      return;
    }
    setBusy(true);
    setError('');
    const { data: updated, error: rpcErr } = await supabase.rpc(
      'mark_application_ready',
      { platform: dmPlatform },
    );
    setBusy(false);
    if (rpcErr) {
      setError(messageFor(rpcErr));
      return;
    }
    setApplication((a) => ({ ...a, ...updated }));
    setScreen('done');
  };

  const stepCount = (i) => `Step ${i + 1} of ${totalDots}`;

  let body;
  if (typeof screen === 'number') {
    const id = INPUT_STAGES[screen];
    if (id === 'email') {
      body = (
        <form className="ap-card" key="email" onSubmit={submitStage}>
          <p className="ap-count">{stepCount(screen)}</p>
          <h1 className="ap-title">What&apos;s your email?</h1>
          <label className="ap-sr" htmlFor="ap-email">
            Email
          </label>
          <input
            ref={inputRef}
            id="ap-email"
            className="ap-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={data.email}
            onChange={(e) => update('email', e.target.value)}
            required
          />
          <p className="ap-help">
            We will send a 6-digit code to this address to verify it.
          </p>
          {error && <p className="ap-error">{error}</p>}
          <div className="ap-actions">
            <Link className="ap-btn ap-btn--ghost" to="/">
              Cancel
            </Link>
            <button
              type="submit"
              className="ap-btn ap-btn--primary"
              disabled={busy}
            >
              {busy ? 'Sending…' : 'Continue'}
            </button>
          </div>
          <button
            type="button"
            className="ap-textlink"
            onClick={() => {
              setError('');
              setScreen('lookup');
            }}
          >
            Already applied? Check your application here
          </button>
        </form>
      );
    } else if (id === 'socials') {
      body = (
        <form className="ap-card" key="socials" onSubmit={submitStage}>
          <p className="ap-count">{stepCount(screen)}</p>
          <h1 className="ap-title">Your social handles</h1>
          <p className="ap-help ap-help--top">
            Fill in the platforms you use. At least one is required.
          </p>
          <div className="ap-fields">
            <label className="ap-field">
              <span className="ap-field__label">Instagram</span>
              <input
                ref={inputRef}
                className="ap-input ap-input--left"
                type="text"
                placeholder="@yourhandle"
                value={data.instagram}
                onChange={(e) => update('instagram', e.target.value)}
              />
            </label>
            <label className="ap-field">
              <span className="ap-field__label">TikTok</span>
              <input
                className="ap-input ap-input--left"
                type="text"
                placeholder="@yourhandle"
                value={data.tiktok}
                onChange={(e) => update('tiktok', e.target.value)}
              />
            </label>
            <label className="ap-field">
              <span className="ap-field__label">X</span>
              <input
                className="ap-input ap-input--left"
                type="text"
                placeholder="@yourhandle"
                value={data.x}
                onChange={(e) => update('x', e.target.value)}
              />
            </label>
          </div>
          {error && <p className="ap-error">{error}</p>}
          <div className="ap-actions">
            {!verified && (
              <button type="button" className="ap-btn ap-btn--ghost" onClick={back}>
                Back
              </button>
            )}
            <button type="submit" className="ap-btn ap-btn--primary">
              Continue
            </button>
          </div>
        </form>
      );
    } else if (id === 'followers') {
      body = (
        <form className="ap-card" key="followers" onSubmit={submitStage}>
          <p className="ap-count">{stepCount(screen)}</p>
          <h1 className="ap-title">How many followers do you have?</h1>
          <p className="ap-help ap-help--top">Your total across all platforms.</p>
          <ul className="ap-options">
            {FOLLOWER_RANGES.map((r) => (
              <li key={r.key}>
                <label
                  className={
                    'ap-option' + (data.followers === r.key ? ' is-selected' : '')
                  }
                >
                  <input
                    type="radio"
                    name="followers"
                    checked={data.followers === r.key}
                    onChange={() => update('followers', r.key)}
                  />
                  <span>{r.label}</span>
                </label>
              </li>
            ))}
          </ul>
          {error && <p className="ap-error">{error}</p>}
          <div className="ap-actions">
            <button type="button" className="ap-btn ap-btn--ghost" onClick={back}>
              Back
            </button>
            <button type="submit" className="ap-btn ap-btn--primary">
              Continue
            </button>
          </div>
        </form>
      );
    } else if (id === 'username') {
      body = (
        <form className="ap-card" key="username" onSubmit={submitStage}>
          <p className="ap-count">{stepCount(screen)}</p>
          <h1 className="ap-title">Choose your username</h1>
          <p className="ap-help ap-help--top">
            Pick one — a custom username or one of your handles.
          </p>
          <ul className="ap-options">
            <li>
              <label
                className={
                  'ap-option' +
                  (data.usernameChoice === 'custom' ? ' is-selected' : '')
                }
              >
                <input
                  type="radio"
                  name="username"
                  checked={data.usernameChoice === 'custom'}
                  onChange={() => update('usernameChoice', 'custom')}
                />
                <span>Custom username</span>
              </label>
              {data.usernameChoice === 'custom' && (
                <input
                  className="ap-input ap-input--left ap-input--nested"
                  type="text"
                  placeholder="yourname"
                  value={data.customUsername}
                  onChange={(e) => update('customUsername', e.target.value)}
                />
              )}
            </li>
            {handleOptions.map((o) => (
              <li key={o.key}>
                <label
                  className={
                    'ap-option' +
                    (data.usernameChoice === o.key ? ' is-selected' : '')
                  }
                >
                  <input
                    type="radio"
                    name="username"
                    checked={data.usernameChoice === o.key}
                    onChange={() => update('usernameChoice', o.key)}
                  />
                  <span>
                    {o.label} — {o.value}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {error && <p className="ap-error">{error}</p>}
          <div className="ap-actions">
            <button
              type="button"
              className="ap-btn ap-btn--ghost"
              onClick={back}
              disabled={busy}
            >
              Back
            </button>
            <button
              type="submit"
              className="ap-btn ap-btn--primary"
              disabled={busy}
            >
              {busy ? 'Submitting…' : 'Continue'}
            </button>
          </div>
        </form>
      );
    } else {
      body = (
        <form className="ap-card" key="emailcode" onSubmit={submitEmailCode}>
          <p className="ap-count">{stepCount(screen)}</p>
          <h1 className="ap-title">Enter your verification code</h1>
          <label className="ap-sr" htmlFor="ap-emailcode">
            Verification code
          </label>
          <input
            ref={inputRef}
            id="ap-emailcode"
            className="ap-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6-digit code"
            value={data.emailcode}
            onChange={(e) => update('emailcode', e.target.value)}
            required
          />
          <p className="ap-help">
            {data.email
              ? `We sent a code to ${data.email}. Enter it to verify your email and continue.`
              : 'Enter the code we sent to your email.'}
          </p>
          {error && <p className="ap-error">{error}</p>}
          <div className="ap-actions">
            <button
              type="button"
              className="ap-btn ap-btn--ghost"
              onClick={back}
              disabled={busy}
            >
              Back
            </button>
            <button
              type="submit"
              className="ap-btn ap-btn--primary"
              disabled={busy}
            >
              {busy ? 'Verifying…' : 'Continue'}
            </button>
          </div>
          <button
            type="button"
            className="ap-textlink"
            onClick={() => resend(data.email.trim(), true)}
            disabled={busy || cooldown > 0}
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
          </button>
        </form>
      );
    }
  } else if (screen === 'lookup') {
    body = (
      <form className="ap-card" key="lookup" onSubmit={submitLookup}>
        <h1 className="ap-title">Welcome back</h1>
        <label className="ap-sr" htmlFor="ap-lookup">
          Email
        </label>
        <input
          ref={inputRef}
          id="ap-lookup"
          className="ap-input"
          type="email"
          inputMode="email"
          placeholder="you@example.com"
          value={lookupValue}
          onChange={(e) => setLookupValue(e.target.value)}
          required
        />
        <p className="ap-help">
          Enter the email you applied with and we will send a code to confirm it
          is you.
        </p>
        {error && <p className="ap-error">{error}</p>}
        <div className="ap-actions">
          <button
            type="button"
            className="ap-btn ap-btn--ghost"
            onClick={() => {
              setError('');
              setScreen(0);
            }}
            disabled={busy}
          >
            Back
          </button>
          <button
            type="submit"
            className="ap-btn ap-btn--primary"
            disabled={busy}
          >
            {busy ? 'Sending…' : 'Continue'}
          </button>
        </div>
      </form>
    );
  } else if (screen === 'lookupcode') {
    body = (
      <form className="ap-card" key="lookupcode" onSubmit={submitLookupCode}>
        <h1 className="ap-title">Enter your verification code</h1>
        <label className="ap-sr" htmlFor="ap-lookupcode">
          Verification code
        </label>
        <input
          ref={inputRef}
          id="ap-lookupcode"
          className="ap-input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="6-digit code"
          value={lookupCode}
          onChange={(e) => setLookupCode(e.target.value)}
          required
        />
        <p className="ap-help">
          {lookupValue
            ? `We sent a code to ${lookupValue}. Enter it to access your application.`
            : 'Enter the code we sent to your email.'}
        </p>
        {error && <p className="ap-error">{error}</p>}
        <div className="ap-actions">
          <button
            type="button"
            className="ap-btn ap-btn--ghost"
            onClick={back}
            disabled={busy}
          >
            Back
          </button>
          <button
            type="submit"
            className="ap-btn ap-btn--primary"
            disabled={busy}
          >
            {busy ? 'Verifying…' : 'Continue'}
          </button>
        </div>
        <button
          type="button"
          className="ap-textlink"
          onClick={() => resend(lookupValue.trim(), false)}
          disabled={busy || cooldown > 0}
        >
          {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
        </button>
      </form>
    );
  } else if (screen === 'verify') {
    body = (
      <div className="ap-card" key="verify">
        <p className="ap-count">Last step</p>
        <h1 className="ap-title">Verify your identity</h1>
        <p className="ap-help">
          Send a direct message with the code below to Fampack on Instagram,
          TikTok, or X. Once we receive it, we will confirm your handles and
          review your application.
        </p>
        <div className="ap-code">{application?.dm_code}</div>
        <button type="button" className="ap-btn ap-btn--ghost ap-copy" onClick={copyCode}>
          {copied ? 'Copied' : 'Copy code'}
        </button>
        <ul className="ap-accounts">
          {FAMPACK_ACCOUNTS.map((a) => (
            <li key={a.key}>
              <span className="ap-accounts__net">{a.network}</span>
              <span className="ap-accounts__handle">{a.handle}</span>
            </li>
          ))}
        </ul>
        <div className="ap-actions">
          <button
            type="button"
            className="ap-btn ap-btn--primary"
            onClick={() => {
              setError('');
              setScreen('platform');
            }}
          >
            I have sent the code
          </button>
        </div>
      </div>
    );
  } else if (screen === 'platform') {
    const picked = accountFor(dmPlatform);
    body = (
      <div className="ap-card" key="platform">
        <h1 className="ap-title">Which account did you send it from?</h1>
        <p className="ap-help ap-help--top">
          Pick the account you sent the message from, so we know where to look.
        </p>
        <ul className="ap-options">
          {platformOptions.map((a) => (
            <li key={a.key}>
              <label
                className={'ap-option' + (dmPlatform === a.key ? ' is-selected' : '')}
              >
                <input
                  type="radio"
                  name="dmplatform"
                  checked={dmPlatform === a.key}
                  onChange={() => {
                    setError('');
                    setDmPlatform(a.key);
                  }}
                />
                <span>
                  {a.network} — {application?.[a.key]}
                </span>
              </label>
            </li>
          ))}
        </ul>

        {picked && (
          <p className="ap-confirm">
            Confirming you sent code <strong>{application?.dm_code}</strong>{' '}
            from your {picked.network} account{' '}
            <strong>{application?.[picked.key]}</strong> to Fampack&apos;s{' '}
            {picked.network} account <strong>{picked.handle}</strong>.
          </p>
        )}

        {error && <p className="ap-error">{error}</p>}
        <div className="ap-actions">
          <button
            type="button"
            className="ap-btn ap-btn--ghost"
            onClick={() => {
              setError('');
              setScreen('verify');
            }}
            disabled={busy}
          >
            Back
          </button>
          <button
            type="button"
            className="ap-btn ap-btn--primary"
            onClick={confirmPlatform}
            disabled={busy || !dmPlatform}
          >
            {busy ? 'Confirming…' : 'Confirm'}
          </button>
        </div>
      </div>
    );
  } else {
    const sentOn = accountFor(application?.dm_platform);
    body = (
      <div className="ap-card" key="done">
        <div className="ap-check" aria-hidden="true">
          ✓
        </div>
        {application?.status === 'approved' ? (
          <>
            <h1 className="ap-title">Application approved</h1>
            <p className="ap-help">
              You are in. Check your email for what happens next.
            </p>
          </>
        ) : application?.status === 'rejected' ? (
          <>
            <h1 className="ap-title">Application closed</h1>
            <p className="ap-help">
              We are not moving forward with this application. Thanks for your
              interest in Fampack.
            </p>
          </>
        ) : application?.status === 'ready' ? (
          <>
            <h1 className="ap-title">Thanks — we are on it</h1>
            <p className="ap-help">
              You have indicated you sent code{' '}
              <strong>{application?.dm_code}</strong> to us on{' '}
              {sentOn ? sentOn.network : 'one of our accounts'}. We will verify
              your handles and get back to you by email.
            </p>
          </>
        ) : (
          <>
            <h1 className="ap-title">Application received</h1>
            <p className="ap-help">
              Thanks. Once we match your message to code{' '}
              <strong>{application?.dm_code}</strong>, we will review your
              application and reply by email.
            </p>
          </>
        )}
        <div className="ap-actions">
          <Link className="ap-btn ap-btn--primary" to="/">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="ap">
      <header className="ap-top">
        <Link className="ap-brand" to="/" aria-label="Fampack home">
          <img className="ap-logo" src="/Logo.svg" alt="" />
          <span className="ap-brand__name">Fampack</span>
        </Link>
        {showDots && <Dots total={totalDots} active={activeDot} />}
      </header>
      <main className="ap-main">
        <div className="ap-stack">
          {body}
          {screen !== 'done' && <AgreementLine />}
        </div>
      </main>
    </div>
  );
}
